import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import findKey from 'lodash/findKey';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { BreadcrumbSection, BreadcrumbDivider, Breadcrumb } from 'semantic-ui-react';
import { Tab, Flex, Link, PageTitle } from 'nallian-shared-ui/lib/components';
import { useNavigate } from 'react-router-dom';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { TABS, FEATURE_TABS, ROLE_TABS } from '../../constants';
import FeatureDetails from '../feature/FeatureDetails';
import FeatureAssignments from '../feature/FeatureAssignments';
import FeatureSubscriptions from '../feature/FeatureSubscriptions';
import RoleDetails from '../role/RoleDetails';
import RoleAssignments from '../role/RoleAssignments';
import FeatureRolesTable from '../feature/FeatureRolesTable';
import AppInstallationCommunities from '../communities/AppInstallationCommunities';
import AppInstallationOrganisations from '../organisations/AppInstallationOrganisations';
import AppInstallationFeatureOverview from './AppInstallationFeatureOverview';
import ContextualTitle from '../ContextualTitle';

const FEATURE_TAB_INDEX_MAPPING = {
  [FEATURE_TABS.DETAILS]: 0,
  [FEATURE_TABS.ROLES]: 1,
  [FEATURE_TABS.ASSIGNMENTS]: 2,
  [FEATURE_TABS.SUBSCRIPTIONS]: 3,
  [FEATURE_TABS.COMMUNITIES]: 4,
};

const ROLE_TAB_INDEX_MAPPING = {
  [ROLE_TABS.DETAILS]: 0,
  [ROLE_TABS.ASSIGNMENTS]: 1,
};

const SubviewFlex = styled(Flex)`
  && .tab {
    box-shadow: none;
    border: none;
    padding-left: 0.2rem;
    padding-right: 0;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  &&& {
    font-size: 0.78571429em !important;
  }
`;

const AppInstallationDetail = ({ appInstallationFetch, appName }) => {
  const { id, installationId, view, viewId, viewPane } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const rights = useSelector(GetRights);

  const { feature, role } = useMemo(() => {
    const flattenedData = (appInstallationFetch.value?.appInstallationFeatures || []).flatMap(f => {
      const roles = f.appInstallationFeatureRoles || [];
      return roles.length > 0 ? roles.map(r => ({ ...r, feature: f })) : [{ feature: f }];
    });

    if (view === 'features') {
      return {
        feature: flattenedData.find(f => f.feature.id === viewId)?.feature,
      };
    }
    if (view === 'roles') {
      const r = flattenedData.find(fr => fr.id === viewId);
      return {
        feature: r?.feature,
        role: r,
      };
    }
    return {};
  }, [view, viewId, appInstallationFetch.value]);

  const TAB_INDEX_MAPPING =
    view === 'features' ? FEATURE_TAB_INDEX_MAPPING : ROLE_TAB_INDEX_MAPPING;

  return (
    <>
      <PageTitle>
        <StyledBreadcrumb size="tiny">
          <BreadcrumbSection>
            <Link to="/apps">{t('apps_plural')}</Link>
          </BreadcrumbSection>
          <BreadcrumbDivider />
          <BreadcrumbSection>
            <Link to={`/apps/${id}`}>{appName}</Link>
          </BreadcrumbSection>
          <BreadcrumbDivider />
          <BreadcrumbSection>
            <Link to={`/apps/${id}/installations`}>{t('installations')}</Link>
          </BreadcrumbSection>
          <BreadcrumbDivider />
          <BreadcrumbSection>
            {role || feature ? (
              <Link to={`/apps/${id}/installations/${installationId}`}>
                {appInstallationFetch.value?.name}
              </Link>
            ) : (
              <span>{appInstallationFetch.value?.name}</span>
            )}
          </BreadcrumbSection>
          {(role || feature) && (
            <>
              <BreadcrumbDivider />
              <BreadcrumbSection>
                {role ? (
                  <Link to={`/apps/${id}/installations/${installationId}/features/${feature.id}`}>
                    {feature?.name}
                  </Link>
                ) : (
                  <strong>{feature?.name}</strong>
                )}
              </BreadcrumbSection>
              {role && (
                <>
                  <BreadcrumbDivider />
                  <BreadcrumbSection>
                    <strong>{role?.name}</strong>
                  </BreadcrumbSection>
                </>
              )}
            </>
          )}
        </StyledBreadcrumb>
      </PageTitle>
      <ContextualTitle
        appInstallation={appInstallationFetch.value}
        feature={feature}
        role={role}
        appName={appName}
        appId={id}
        installationId={installationId}
      />
      <Flex fluid direction="column">
        {viewId ? (
          <SubviewFlex fluid direction="column">
            <Tab
              activeIndex={TAB_INDEX_MAPPING[viewPane]}
              onTabChange={(e, { activeIndex }) => {
                const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
                if (activeTab) {
                  navigate(
                    `/apps/${id}/${TABS.INSTALLATIONS}/${installationId}/${view}/${viewId}/${activeTab}`
                  );
                }
              }}
              panes={
                view === 'features'
                  ? [
                      {
                        menuItem: t('details'),
                        render: () => (
                          <Tab.Pane>
                            <FeatureDetails
                              feature={feature}
                              isLoading={appInstallationFetch.pending}
                            />
                          </Tab.Pane>
                        ),
                      },
                      {
                        menuItem: t('roles'),
                        render: () => (
                          <Tab.Pane>
                            <FeatureRolesTable
                              rootUrl={`/apps/${id}/${TABS.INSTALLATIONS}/${installationId}`}
                              roles={feature?.appInstallationFeatureRoles}
                              appId={id}
                              installationId={installationId}
                              featureId={feature?.id}
                              appInstallationFetch={appInstallationFetch}
                            />
                          </Tab.Pane>
                        ),
                      },
                      hasRight(rights, 'apps/installations/feature/assignments/read')
                        ? {
                            menuItem: t('assignments'),
                            render: () => (
                              <Tab.Pane>
                                <FeatureAssignments
                                  appId={id}
                                  installationId={installationId}
                                  featureId={feature?.id}
                                />
                              </Tab.Pane>
                            ),
                          }
                        : undefined,
                      hasRight(rights, 'apps/installations/features/subscriptions/read') &&
                      !(feature?.isGlobal && !feature?.requiresSubscription)
                        ? {
                            menuItem: t('subscriptions'),
                            render: () => (
                              <Tab.Pane>
                                <FeatureSubscriptions
                                  appId={id}
                                  installationId={installationId}
                                  featureId={feature?.id}
                                  installationName={appInstallationFetch.value?.name}
                                  appName={appName}
                                  canCreate={feature?.requiresSubscription && feature?.isGlobal}
                                  feature={feature}
                                />
                              </Tab.Pane>
                            ),
                          }
                        : undefined,
                    ].filter(p => p)
                  : [
                      {
                        menuItem: t('details'),
                        render: () => (
                          <Tab.Pane>
                            <RoleDetails role={role} isLoading={appInstallationFetch.pending} />
                          </Tab.Pane>
                        ),
                      },
                      hasRight(rights, 'apps/instances/features/roles/assignments/read')
                        ? {
                            menuItem: t('assignments'),
                            render: () => (
                              <Tab.Pane>
                                <RoleAssignments
                                  appId={id}
                                  installationId={installationId}
                                  featureId={feature?.id}
                                  roleId={role?.id}
                                  isGlobal={feature?.isGlobal}
                                />
                              </Tab.Pane>
                            ),
                          }
                        : undefined,
                    ].filter(p => p)
              }
            />
          </SubviewFlex>
        ) : (
          <Tab
            panes={[
              {
                menuItem: t('features'),
                render: () => (
                  <Tab.Pane>
                    <AppInstallationFeatureOverview
                      appInstallationFetch={appInstallationFetch}
                      appId={id}
                      installationId={installationId}
                    />
                  </Tab.Pane>
                ),
              },
              {
                menuItem: t('communities'),
                render: () => (
                  <Tab.Pane>
                    <AppInstallationCommunities />
                  </Tab.Pane>
                ),
              },
              hasRight(rights, 'apps/organisations/read')
                ? {
                    menuItem: t('organisations'),
                    render: () => (
                      <Tab.Pane>
                        <AppInstallationOrganisations />
                      </Tab.Pane>
                    ),
                  }
                : undefined,
            ].filter(p => p)}
          />
        )}
      </Flex>
    </>
  );
};

AppInstallationDetail.propTypes = {
  appInstallationFetch: PropTypes.object.isRequired,
  appName: PropTypes.string.isRequired,
};

export default AppInstallationDetail;
